import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './Admin.css';

function Admin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    icon: '⚡',
    image_url: '',
    price: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [isDraggingHeroImage, setIsDraggingHeroImage] = useState(false);

  const [activeTab, setActiveTab] = useState('products'); // 'products', 'page-content', or 'categories'
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [pageContentForm, setPageContentForm] = useState({
    hero_badge: '',
    hero_title: '',
    hero_subtitle: '',
    hero_description: '',
    hero_button1: '',
    hero_button2: '',
    products_title: '',
    products_subtitle: '',
    about_title: '',
    about_paragraph1: '',
    about_paragraph2: '',
    contact_title: '',
    contact_subtitle: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    footer_text: '',
    logo_url: '',
    hero_image_url: '',
    whatsapp_phone: ''
  });

  // Fetch products from Supabase
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Ürünler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch page content from Supabase
  const fetchPageContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('page_content')
        .select('*')
        .eq('id', 1)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (data) {
        setPageContentForm(data);
      }
    } catch (err) {
      console.error('Error fetching page content:', err);
      setError(err.message || 'Sayfa içeriği yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories from Supabase
  const fetchCategories = async () => {
    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message || 'Kategoriler yüklenirken bir hata oluştu.');
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    checkUser();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setIsAuthenticated(true);
        setUser(session.user);
        fetchProducts();
        fetchPageContent();
        fetchCategories();
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setProducts([]);
        setCategories([]);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setIsAuthenticated(true);
        setUser(session.user);
        fetchProducts();
        fetchPageContent();
        fetchCategories();
      }
    } catch (err) {
      console.error('Error checking session:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (authError) throw authError;

      if (data.user) {
        setIsAuthenticated(true);
        setUser(data.user);
        await fetchProducts();
        await fetchPageContent();
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Giriş yapılırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setIsAuthenticated(false);
      setUser(null);
      setEmail('');
      setPassword('');
      setProducts([]);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      alert('Çıkış yapılırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct('new'); // Use 'new' to indicate adding a new product
    const firstCategory = categories.length > 0 ? categories[0].name : '';
    setFormData({
      name: '',
      category: firstCategory,
      description: '',
      icon: '⚡',
      image_url: '',
      price: ''
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product.id);
    setFormData({
      name: product.name,
      category: product.category,
      description: product.description,
      icon: product.icon || '⚡',
      image_url: product.image_url || '',
      price: product.price || ''
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Lütfen bir resim dosyası seçin.');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `products/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setFormData({ ...formData, image_url: data.publicUrl });
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Resim yüklenirken bir hata oluştu.');
      alert('Resim yüklenirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Lütfen bir resim dosyası seçin.');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `logo_${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setPageContentForm({ ...pageContentForm, logo_url: data.publicUrl });
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.message || 'Logo yüklenirken bir hata oluştu.');
      alert('Logo yüklenirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const handleLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const handleHeroImageUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Lütfen bir resim dosyası seçin.');
      return;
    }

    try {
      setUploadingHeroImage(true);
      setError(null);

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `hero_${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `hero/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setPageContentForm({ ...pageContentForm, hero_image_url: data.publicUrl });
    } catch (err) {
      console.error('Error uploading hero image:', err);
      setError(err.message || 'Hero resmi yüklenirken bir hata oluştu.');
      alert('Hero resmi yüklenirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const handleHeroImageFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleHeroImageUpload(file);
    }
  };

  const handleHeroImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingHeroImage(true);
  };

  const handleHeroImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingHeroImage(false);
  };

  const handleHeroImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingHeroImage(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleHeroImageUpload(file);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) {
      try {
        setLoading(true);
        setError(null);
        const { error: deleteError } = await supabase
          .from('products')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;
        
        // Refresh products list
        await fetchProducts();
        alert('Ürün başarıyla silindi.');
      } catch (err) {
        console.error('Error deleting product:', err);
        setError(err.message || 'Ürün silinirken bir hata oluştu.');
        alert('Ürün silinirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Build the data object, only include image_url and price if they exist and the column exists
      const productData = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        icon: formData.icon
      };
      
      // Only add image_url if it has a value (assumes column exists after running SQL)
      if (formData.image_url && formData.image_url.trim() !== '') {
        productData.image_url = formData.image_url;
      }

      // Only add price if it has a value (assumes column exists after running SQL)
      if (formData.price && formData.price.trim() !== '') {
        productData.price = formData.price.trim();
      }

      if (editingProduct === 'new') {
        // Add new product
        const { error: insertError } = await supabase
          .from('products')
          .insert([productData]);

        if (insertError) throw insertError;
        alert('Ürün başarıyla eklendi.');
      } else {
        // Update existing product
        const { error: updateError } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct);

        if (updateError) throw updateError;
        alert('Ürün başarıyla güncellendi.');
      }

      // Refresh products list
      await fetchProducts();
      
      // Reset form
      setEditingProduct(null);
      const firstCategory = categories.length > 0 ? categories[0].name : '';
      setFormData({
        name: '',
        category: firstCategory,
        description: '',
        icon: '⚡',
        image_url: '',
        price: ''
      });
    } catch (err) {
      console.error('Error saving product:', err);
      setError(err.message || 'Ürün kaydedilirken bir hata oluştu.');
      alert('Ürün kaydedilirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePageContent = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Build the data object, only include logo_url if it exists and has a value
      const pageContentData = {
        hero_badge: pageContentForm.hero_badge,
        hero_title: pageContentForm.hero_title,
        hero_subtitle: pageContentForm.hero_subtitle,
        hero_description: pageContentForm.hero_description,
        hero_button1: pageContentForm.hero_button1,
        hero_button2: pageContentForm.hero_button2,
        products_title: pageContentForm.products_title,
        products_subtitle: pageContentForm.products_subtitle,
        about_title: pageContentForm.about_title,
        about_paragraph1: pageContentForm.about_paragraph1,
        about_paragraph2: pageContentForm.about_paragraph2,
        contact_title: pageContentForm.contact_title,
        contact_subtitle: pageContentForm.contact_subtitle,
        contact_email: pageContentForm.contact_email,
        contact_phone: pageContentForm.contact_phone,
        contact_address: pageContentForm.contact_address,
        footer_text: pageContentForm.footer_text
      };

      // Only add logo_url if it has a value (assumes column exists after running SQL)
      if (pageContentForm.logo_url && pageContentForm.logo_url.trim() !== '') {
        pageContentData.logo_url = pageContentForm.logo_url;
      }

      // Only add hero_image_url if it has a value (assumes column exists after running SQL)
      if (pageContentForm.hero_image_url && pageContentForm.hero_image_url.trim() !== '') {
        pageContentData.hero_image_url = pageContentForm.hero_image_url;
      }

      // Only add whatsapp_phone if it has a value (assumes column exists after running SQL)
      if (pageContentForm.whatsapp_phone && pageContentForm.whatsapp_phone.trim() !== '') {
        pageContentData.whatsapp_phone = pageContentForm.whatsapp_phone;
      }

      // First, check if row exists
      const { data: existing } = await supabase
        .from('page_content')
        .select('id')
        .eq('id', 1)
        .single();

      let error;
      if (existing) {
        // Update existing row
        const { error: updateError } = await supabase
          .from('page_content')
          .update(pageContentData)
          .eq('id', 1);

        error = updateError;
      } else {
        // Insert new row
        const { error: insertError } = await supabase
          .from('page_content')
          .insert({
            id: 1,
            ...pageContentData
          });

        error = insertError;
      }

      if (error) throw error;
      alert('Sayfa içeriği başarıyla kaydedildi!');
      await fetchPageContent();
    } catch (err) {
      console.error('Error saving page content:', err);
      setError(err.message || 'Sayfa içeriği kaydedilirken bir hata oluştu.');
      alert('Sayfa içeriği kaydedilirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory('new');
    setCategoryName('');
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setCategoryName(category.name);
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Bu kategoriyi silmek istediğinize emin misiniz? Bu kategorideki ürünler etkilenebilir.')) {
      try {
        setLoading(true);
        setError(null);
        const { error: deleteError } = await supabase
          .from('categories')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;
        
        await fetchCategories();
        alert('Kategori başarıyla silindi.');
      } catch (err) {
        console.error('Error deleting category:', err);
        setError(err.message || 'Kategori silinirken bir hata oluştu.');
        alert('Kategori silinirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      alert('Kategori adı boş olamaz!');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingCategory === 'new') {
        // Add new category
        const { error: insertError } = await supabase
          .from('categories')
          .insert([{ name: categoryName.trim() }]);

        if (insertError) throw insertError;
        alert('Kategori başarıyla eklendi.');
      } else {
        // Update existing category
        const { error: updateError } = await supabase
          .from('categories')
          .update({ name: categoryName.trim() })
          .eq('id', editingCategory);

        if (updateError) throw updateError;
        alert('Kategori başarıyla güncellendi.');
      }

      await fetchCategories();
      setEditingCategory(null);
      setCategoryName('');
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Kategori kaydedilirken bir hata oluştu.');
      alert('Kategori kaydedilirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <div className="login-header">
            <h1>BORMAK</h1>
            <p>Admin Paneli</p>
          </div>
          <form onSubmit={handleLogin} className="login-form">
            {error && (
              <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem' }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>⚠️ {error}</p>
              </div>
            )}
            <div className="form-group">
              <label>E-posta</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@example.com"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>Şifre</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Get category names from fetched categories
  const categoryNames = categories.map(cat => cat.name);
  const groupedProducts = categoryNames.map(catName => ({
    category: catName,
    products: products.filter(p => p.category === catName)
  }));

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1>BORMAK Admin Paneli</h1>
            {user && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                {user.email}
              </p>
            )}
          </div>
          <div className="admin-actions">
            <button onClick={() => navigate('/')} className="btn btn-secondary">
              Ana Sayfaya Dön
            </button>
            <button onClick={handleLogout} className="btn btn-danger" disabled={loading}>
              {loading ? 'Çıkılıyor...' : 'Çıkış Yap'}
            </button>
          </div>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-sidebar">
          <div className="admin-stats">
            <div className="stat-card">
              <h3>{products.length}</h3>
              <p>Toplam Ürün</p>
            </div>
            <div className="stat-card">
              <h3>{categories.length}</h3>
              <p>Kategori</p>
            </div>
          </div>
          <div className="admin-actions-sidebar">
            <div className="admin-tabs">
              <button 
                onClick={() => setActiveTab('products')}
                className={`btn btn-full ${activeTab === 'products' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ marginBottom: '0.5rem' }}
              >
                Ürünler
              </button>
              <button 
                onClick={() => setActiveTab('categories')}
                className={`btn btn-full ${activeTab === 'categories' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ marginBottom: '0.5rem' }}
              >
                Kategoriler
              </button>
              <button 
                onClick={() => setActiveTab('page-content')}
                className={`btn btn-full ${activeTab === 'page-content' ? 'btn-primary' : 'btn-secondary'}`}
              >
                Sayfa İçeriği
              </button>
            </div>
            {activeTab === 'products' && (
              <button 
                onClick={handleAddProduct} 
                className="btn btn-primary btn-full"
                disabled={loading}
                style={{ marginTop: '1rem' }}
              >
                + Yeni Ürün Ekle
              </button>
            )}
            {activeTab === 'categories' && (
              <button 
                onClick={handleAddCategory} 
                className="btn btn-primary btn-full"
                disabled={loading}
                style={{ marginTop: '1rem' }}
              >
                + Yeni Kategori Ekle
              </button>
            )}
          </div>
        </div>

        <div className="admin-main">
          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
              <button onClick={() => setError(null)} className="btn btn-small">Kapat</button>
            </div>
          )}
          
          {loading && !error && (
            <div className="loading-message">
              <p>Yükleniyor...</p>
            </div>
          )}

          {activeTab === 'page-content' && (
            <div className="admin-form-panel">
              <h2>Sayfa İçeriğini Düzenle</h2>
              <form onSubmit={handleSavePageContent} className="admin-form">
                <div className="form-section">
                  <h3>Logo</h3>
                  <div className="form-group">
                    <label>Logo (Navbar'da görünecek)</label>
                    <div
                      className={`drag-drop-zone ${isDraggingLogo ? 'dragging' : ''}`}
                      onDragOver={handleLogoDragOver}
                      onDragLeave={handleLogoDragLeave}
                      onDrop={handleLogoDrop}
                    >
                      <div className="drag-drop-content">
                        <div className="drag-drop-icon">📁</div>
                        <p className="drag-drop-text">
                          {isDraggingLogo ? 'Dosyayı buraya bırakın' : 'Dosyayı buraya sürükleyip bırakın veya'}
                        </p>
                        <label htmlFor="logo-file-input" className="drag-drop-button">
                          Logo Seç
                        </label>
                        <input
                          id="logo-file-input"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoFileInputChange}
                          disabled={uploadingLogo}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>
                    {uploadingLogo && <p style={{ color: '#666', marginTop: '0.5rem' }}>Yükleniyor...</p>}
                    {pageContentForm.logo_url && (
                      <div style={{ marginTop: '1rem' }}>
                        <img 
                          src={pageContentForm.logo_url} 
                          alt="Logo Preview" 
                          style={{ maxWidth: '200px', maxHeight: '100px', borderRadius: '8px', border: '2px solid #e5e7eb' }}
                        />
                        <button
                          type="button"
                          onClick={() => setPageContentForm({ ...pageContentForm, logo_url: '' })}
                          style={{ marginLeft: '1rem', padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Logoyu Kaldır
                        </button>
                      </div>
                    )}
                    <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      Logo yüklemek için dosyayı sürükleyip bırakın veya dosya seç butonuna tıklayın.
                    </p>
                  </div>
                </div>
                <div className="form-section">
                  <h3>Hero Bölümü</h3>
                  <div className="form-group">
                    <label>Hero Arka Plan Resmi</label>
                    <div
                      className={`drag-drop-zone ${isDraggingHeroImage ? 'dragging' : ''}`}
                      onDragOver={handleHeroImageDragOver}
                      onDragLeave={handleHeroImageDragLeave}
                      onDrop={handleHeroImageDrop}
                    >
                      <div className="drag-drop-content">
                        <div className="drag-drop-icon">📁</div>
                        <p className="drag-drop-text">
                          {isDraggingHeroImage ? 'Dosyayı buraya bırakın' : 'Dosyayı buraya sürükleyip bırakın veya'}
                        </p>
                        <label htmlFor="hero-image-file-input" className="drag-drop-button">
                          Resim Seç
                        </label>
                        <input
                          id="hero-image-file-input"
                          type="file"
                          accept="image/*"
                          onChange={handleHeroImageFileInputChange}
                          disabled={uploadingHeroImage}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>
                    {uploadingHeroImage && <p style={{ color: '#666', marginTop: '0.5rem' }}>Yükleniyor...</p>}
                    {pageContentForm.hero_image_url && (
                      <div style={{ marginTop: '1rem' }}>
                        <img 
                          src={pageContentForm.hero_image_url} 
                          alt="Hero Preview" 
                          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', border: '2px solid #e5e7eb' }}
                        />
                        <button
                          type="button"
                          onClick={() => setPageContentForm({ ...pageContentForm, hero_image_url: '' })}
                          style={{ marginLeft: '1rem', padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Resmi Kaldır
                        </button>
                      </div>
                    )}
                    <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      Hero bölümü arka plan resmi yüklemek için dosyayı sürükleyip bırakın veya dosya seç butonuna tıklayın.
                    </p>
                  </div>
                  <div className="form-group">
                    <label>Hero Badge</label>
                    <input
                      type="text"
                      value={pageContentForm.hero_badge}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, hero_badge: e.target.value })}
                      placeholder="ALTINEL PLASTİK"
                    />
                  </div>
                  <div className="form-group">
                    <label>Hero Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.hero_title}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, hero_title: e.target.value })}
                      placeholder="BORMAK"
                    />
                  </div>
                  <div className="form-group">
                    <label>Hero Alt Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.hero_subtitle}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, hero_subtitle: e.target.value })}
                      placeholder="Plastik Boru Kaynak Makinası"
                    />
                  </div>
                  <div className="form-group">
                    <label>Hero Açıklama</label>
                    <textarea
                      value={pageContentForm.hero_description}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, hero_description: e.target.value })}
                      rows="3"
                      placeholder="Profesyonel plastik boru kaynak çözümleri..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Buton 1 Metni</label>
                    <input
                      type="text"
                      value={pageContentForm.hero_button1}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, hero_button1: e.target.value })}
                      placeholder="Ürünleri İncele"
                    />
                  </div>
                  <div className="form-group">
                    <label>Buton 2 Metni</label>
                    <input
                      type="text"
                      value={pageContentForm.hero_button2}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, hero_button2: e.target.value })}
                      placeholder="Fiyat Bilgisi Al"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Ürünler Bölümü</h3>
                  <div className="form-group">
                    <label>Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.products_title}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, products_title: e.target.value })}
                      placeholder="Ürün Kataloğumuz"
                    />
                  </div>
                  <div className="form-group">
                    <label>Alt Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.products_subtitle}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, products_subtitle: e.target.value })}
                      placeholder="Geniş ürün yelpazemizle..."
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Hakkımızda Bölümü</h3>
                  <div className="form-group">
                    <label>Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.about_title}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, about_title: e.target.value })}
                      placeholder="Hakkımızda"
                    />
                  </div>
                  <div className="form-group">
                    <label>Paragraf 1</label>
                    <textarea
                      value={pageContentForm.about_paragraph1}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, about_paragraph1: e.target.value })}
                      rows="4"
                      placeholder="İlk paragraf..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Paragraf 2</label>
                    <textarea
                      value={pageContentForm.about_paragraph2}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, about_paragraph2: e.target.value })}
                      rows="4"
                      placeholder="İkinci paragraf..."
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>İletişim Bölümü</h3>
                  <div className="form-group">
                    <label>Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.contact_title}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, contact_title: e.target.value })}
                      placeholder="İletişim"
                    />
                  </div>
                  <div className="form-group">
                    <label>Alt Başlık</label>
                    <input
                      type="text"
                      value={pageContentForm.contact_subtitle}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, contact_subtitle: e.target.value })}
                      placeholder="Fiyat bilgisi için..."
                    />
                  </div>
                  <div className="form-group">
                    <label>E-posta</label>
                    <input
                      type="email"
                      value={pageContentForm.contact_email}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, contact_email: e.target.value })}
                      placeholder="info@bormak.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Telefon</label>
                    <input
                      type="text"
                      value={pageContentForm.contact_phone}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, contact_phone: e.target.value })}
                      placeholder="+90 (XXX) XXX XX XX"
                    />
                  </div>
                  <div className="form-group">
                    <label>Adres (Satırlar için \n kullanın)</label>
                    <textarea
                      value={pageContentForm.contact_address}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, contact_address: e.target.value })}
                      rows="3"
                      placeholder="Altinel Plastik&#10;İstanbul, Türkiye"
                    />
                  </div>
                  <div className="form-group">
                    <label>WhatsApp Telefon Numarası (Fiyat olmayan ürünler için)</label>
                    <input
                      type="text"
                      value={pageContentForm.whatsapp_phone}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, whatsapp_phone: e.target.value })}
                      placeholder="905551234567 (90 ile başlayan, başında + olmadan)"
                    />
                    <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      Örnek: 905551234567 (ülke kodu + telefon numarası, başında + işareti olmadan)
                    </p>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Footer</h3>
                  <div className="form-group">
                    <label>Footer Metni</label>
                    <input
                      type="text"
                      value={pageContentForm.footer_text}
                      onChange={(e) => setPageContentForm({ ...pageContentForm, footer_text: e.target.value })}
                      placeholder="© 2024 BORMAK..."
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {activeTab === 'products' && editingProduct !== null && (
            <div className="admin-form-panel">
              <h2>{editingProduct === 'new' ? 'Yeni Ürün Ekle' : 'Ürün Düzenle'}</h2>
              <form onSubmit={handleSaveProduct} className="admin-form">
                <div className="form-group">
                  <label>Ürün Adı</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Örn: BORMAK 01 SET 2000W"
                  />
                </div>
                <div className="form-group">
                  <label>Kategori</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categoryNames.length > 0 ? (
                      categoryNames.map(catName => (
                        <option key={catName} value={catName}>{catName}</option>
                      ))
                    ) : (
                      <option value="">Kategori yok - Önce kategori ekleyin</option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows="4"
                    placeholder="Ürün açıklaması..."
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Resmi</label>
                  <div
                    className={`drag-drop-zone ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="drag-drop-content">
                      <div className="drag-drop-icon">📁</div>
                      <p className="drag-drop-text">
                        {isDragging ? 'Dosyayı buraya bırakın' : 'Dosyayı buraya sürükleyip bırakın veya'}
                      </p>
                      <label htmlFor="file-input" className="drag-drop-button">
                        Dosya Seç
                      </label>
                      <input
                        id="file-input"
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        disabled={uploadingImage}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                  {uploadingImage && <p style={{ color: '#666', marginTop: '0.5rem' }}>Yükleniyor...</p>}
                  {formData.image_url && (
                    <div style={{ marginTop: '1rem' }}>
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '2px solid #e5e7eb' }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        style={{ marginLeft: '1rem', padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Resmi Kaldır
                      </button>
                    </div>
                  )}
                  <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    Resim yüklemek için dosyayı sürükleyip bırakın veya dosya seç butonuna tıklayın.
                  </p>
                </div>
                <div className="form-group">
                  <label>İkon (Emoji) - Resim yoksa kullanılır</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="⚡"
                    maxLength="2"
                  />
                </div>
                <div className="form-group">
                  <label>Fiyat (TL) - Boş bırakılırsa WhatsApp iletişim butonu gösterilir</label>
                  <input
                    type="text"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Örn: 1.500 TL veya 2.500,00 TL"
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Kaydediliyor...' : (editingProduct === 'new' ? 'Ekle' : 'Güncelle')}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setEditingProduct(null);
                      const firstCategory = categories.length > 0 ? categories[0].name : '';
                      setFormData({
                        name: '',
                        category: firstCategory,
                        description: '',
                        icon: '⚡',
                        image_url: ''
                      });
                    }}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="admin-products">
              {groupedProducts.map(({ category, products: categoryProducts }) => (
              <div key={category} className="product-category-section">
                <h2 className="category-header">{category}</h2>
                {categoryProducts.length === 0 ? (
                  <p className="no-products">Bu kategoride ürün bulunmamaktadır.</p>
                ) : (
                  <div className="admin-product-grid">
                    {categoryProducts.map(product => (
                      <div key={product.id} className="admin-product-card">
                        {product.image_url ? (
                          <div className="product-image-container">
                            <img src={product.image_url} alt={product.name} className="product-image" />
                          </div>
                        ) : (
                          <div className="product-icon-large">{product.icon || '⚡'}</div>
                        )}
                        <h3>{product.name}</h3>
                        <p>{product.description}</p>
                        <div className="product-actions">
                          <button 
                            onClick={() => handleEditProduct(product)}
                            className="btn btn-small btn-primary"
                          >
                            Düzenle
                          </button>
                          <button 
                            onClick={() => handleDeleteProduct(product.id)}
                            className="btn btn-small btn-danger"
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="admin-products">
              {editingCategory !== null && (
                <div className="admin-form-panel" style={{ marginBottom: '2rem' }}>
                  <h2>{editingCategory === 'new' ? 'Yeni Kategori Ekle' : 'Kategori Düzenle'}</h2>
                  <form onSubmit={handleSaveCategory} className="admin-form">
                    <div className="form-group">
                      <label>Kategori Adı</label>
                      <input
                        type="text"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        required
                        placeholder="Örn: Kaynak Makineleri"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Kaydediliyor...' : (editingCategory === 'new' ? 'Ekle' : 'Güncelle')}
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setEditingCategory(null);
                          setCategoryName('');
                        }}
                        className="btn btn-secondary"
                        disabled={loading}
                      >
                        İptal
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="product-category-section">
                <h2 className="category-header">Kategoriler</h2>
                {categories.length === 0 ? (
                  <p className="no-products">Henüz kategori eklenmemiş.</p>
                ) : (
                  <div className="admin-product-grid">
                    {categories.map(category => (
                      <div key={category.id} className="admin-product-card">
                        <h3>{category.name}</h3>
                        <p style={{ color: '#666', fontSize: '0.9rem' }}>
                          {products.filter(p => p.category === category.name).length} ürün
                        </p>
                        <div className="product-actions">
                          <button 
                            onClick={() => handleEditCategory(category)}
                            className="btn btn-small btn-primary"
                          >
                            Düzenle
                          </button>
                          <button 
                            onClick={() => handleDeleteCategory(category.id)}
                            className="btn btn-small btn-danger"
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Admin;

